// ---------------------------------------------------------------------------
// narrationCoverage: where the delivered film has picture but no voice
// (RNC-014, plan 2026-10-12 §4.9 D9, contract `narration-coverage.v1`).
//
// The composer will write `narration.coverage` to `echo-state.v1.json` after
// the measured fit (RNC-013). When it is there, it is the authority and is
// shown as is. Until then (and for every Echo rendered before it), the same
// silence map is computed here from fields the manifest already carries:
//
//   - `realized_scene_timeline[]`: scene_id, scene_type, start/end second;
//   - `narration.timings[]`: where each line was actually placed
//     (`actual_start`..`end`);
//   - `content_duration_seconds`: content ends here, the logo outro follows.
//
// Rules (contract §5): only non-title picture counts, so title cards and the
// outro/logo tail are exempt; a stretch is listed from 2.0 s; 6.0 s is the
// reporting constant the composer's `voiceover_gap_over_picture` info issue
// uses. Neither number is a decision: policy lives in the orchestrator.
//
// Everything below is CONTENT seconds (the manifest's frame of reference);
// callers add `deliveredOffsetSeconds` to show or seek delivered-file time.
// ---------------------------------------------------------------------------

export const NARRATION_COVERAGE_SCHEMA = "narration-coverage.v1";

/** A silent stretch is listed from this length (contract §5). */
export const MIN_SILENT_STRETCH_SECONDS = 2.0;

/** Reporting constant: a stretch this long is a `voiceover_gap_over_picture`. */
export const LONG_SILENCE_SECONDS = 6.0;

/** Intervals closer than this are one interval (float noise from the composer). */
const TOUCH_EPSILON_SECONDS = 0.001;

export type NarrationCoverageSource = "manifest" | "computed";

export type NarrationCoverageStretch = {
  /** Content seconds. */
  start: number;
  end: number;
  seconds: number;
  /** Non-title scenes the stretch plays over, in timeline order. */
  sceneIds: number[];
};

export type NarrationCoverage = {
  /** `manifest`: the composer's measured metric; `computed`: derived here. */
  source: NarrationCoverageSource;
  speechSeconds: number;
  pictureSeconds: number;
  /** Longest silence over counted picture, listed or not (0 when none). */
  longestSeconds: number;
  /** Stretches of at least `MIN_SILENT_STRETCH_SECONDS`, by start time. */
  stretches: NarrationCoverageStretch[];
  /** Add to content seconds for delivered-file time (poster frame). */
  deliveredOffsetSeconds: number;
};

type Row = Record<string, unknown>;
type Interval = { start: number; end: number };

const row = (value: unknown): Row | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Row)
    : null;

const num = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const round2 = (value: number) => Math.round(value * 100) / 100;

function sceneId(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return Number(value.trim());
  }
  return null;
}

/** Title cards, the agency outro and the logo tail are exempt picture. */
export function isExemptSceneType(sceneType: unknown): boolean {
  return typeof sceneType === "string" && /title|outro|logo/i.test(sceneType);
}

// ---------------------------------------------------------------------------
// Interval arithmetic (sorted, merged, half-open in spirit)
// ---------------------------------------------------------------------------

function union(intervals: Interval[]): Interval[] {
  const sorted = intervals
    .filter((interval) => interval.end > interval.start)
    .sort((left, right) => left.start - right.start);
  const merged: Interval[] = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.start <= last.end + TOUCH_EPSILON_SECONDS) {
      last.end = Math.max(last.end, interval.end);
    } else {
      merged.push({ ...interval });
    }
  }
  return merged;
}

/** `from` minus `cut`; both must already be unions. */
function subtract(from: Interval[], cut: Interval[]): Interval[] {
  const result: Interval[] = [];
  for (const interval of from) {
    let cursor = interval.start;
    for (const hole of cut) {
      if (hole.end <= cursor) continue;
      if (hole.start >= interval.end) break;
      if (hole.start > cursor) result.push({ start: cursor, end: hole.start });
      cursor = Math.max(cursor, hole.end);
      if (cursor >= interval.end) break;
    }
    if (cursor < interval.end)
      result.push({ start: cursor, end: interval.end });
  }
  return result.filter(
    (interval) => interval.end - interval.start > TOUCH_EPSILON_SECONDS,
  );
}

const measure = (intervals: Interval[]) =>
  intervals.reduce((sum, interval) => sum + (interval.end - interval.start), 0);

const overlap = (left: Interval, right: Interval) =>
  Math.max(
    0,
    Math.min(left.end, right.end) - Math.max(left.start, right.start),
  );

// ---------------------------------------------------------------------------
// Computation
// ---------------------------------------------------------------------------

export type CoverageScene = {
  sceneId: number | null;
  sceneType: string | null;
  start: number;
  end: number;
};

export type CoverageInputs = {
  /** Realized scenes, content seconds. */
  scenes: CoverageScene[];
  /** Realized narration placements, content seconds. */
  speech: Interval[];
  /** Content end (the logo outro starts here); defaults to the last scene end. */
  contentDurationSeconds?: number | null;
};

export type ComputedCoverage = Omit<
  NarrationCoverage,
  "source" | "deliveredOffsetSeconds"
>;

/**
 * The silence map: counted picture (non-title scenes, before the outro) minus
 * speech. Pure and deterministic. Returns null when there is no counted
 * picture to measure.
 */
export function computeNarrationCoverage(
  inputs: CoverageInputs,
): ComputedCoverage | null {
  const lastEnd = inputs.scenes.reduce(
    (max, scene) => Math.max(max, scene.end),
    0,
  );
  const contentEnd =
    num(inputs.contentDurationSeconds) != null &&
    (inputs.contentDurationSeconds as number) > 0
      ? (inputs.contentDurationSeconds as number)
      : lastEnd;
  const clip = (interval: Interval): Interval => ({
    start: Math.max(0, interval.start),
    end: Math.min(contentEnd, interval.end),
  });

  const counted = inputs.scenes.filter(
    (scene) => !isExemptSceneType(scene.sceneType),
  );
  // Exempt time wins wherever it is: a crossfade into a title card is not
  // picture the narrator owes words to.
  const exempt = union(
    inputs.scenes
      .filter((scene) => isExemptSceneType(scene.sceneType))
      .map((scene) => clip(scene)),
  );
  const picture = subtract(union(counted.map((scene) => clip(scene))), exempt);
  const pictureSeconds = measure(picture);
  if (pictureSeconds <= 0) return null;

  const speech = union(inputs.speech.map(clip));
  const silence = subtract(picture, speech);
  const speechSeconds = pictureSeconds - measure(silence);

  const longestSeconds = silence.reduce(
    (max, interval) => Math.max(max, interval.end - interval.start),
    0,
  );

  const stretches = silence
    .filter(
      (interval) =>
        // Compare on the rounded value the operator sees, so 1.999 s is not
        // listed while 2.00 s on screen would be.
        round2(interval.end - interval.start) >= MIN_SILENT_STRETCH_SECONDS,
    )
    .map((interval) => {
      const sceneIds: number[] = [];
      for (const scene of [...counted].sort((a, b) => a.start - b.start)) {
        if (scene.sceneId == null || sceneIds.includes(scene.sceneId)) continue;
        // Any real overlap names the scene: the contract's golden names
        // scene 10 for the 0.1 s it shares with the 27.64–38.13 s stretch.
        if (overlap(interval, scene) > TOUCH_EPSILON_SECONDS) {
          sceneIds.push(scene.sceneId);
        }
      }
      return {
        start: round2(interval.start),
        end: round2(interval.end),
        seconds: round2(interval.end - interval.start),
        sceneIds,
      };
    });

  return {
    speechSeconds: round2(speechSeconds),
    pictureSeconds: round2(pictureSeconds),
    longestSeconds: round2(longestSeconds),
    stretches,
  };
}

// ---------------------------------------------------------------------------
// Manifest readers
// ---------------------------------------------------------------------------

/**
 * Parse the composer's `narration.coverage` (contract §5). Anything that does
 * not match the contract is ignored (null), never half-trusted.
 */
export function parseManifestCoverage(value: unknown): ComputedCoverage | null {
  const coverage = row(value);
  if (coverage?.schema_version !== NARRATION_COVERAGE_SCHEMA) return null;
  const speechSeconds = num(coverage.speech_seconds);
  const pictureSeconds = num(coverage.picture_seconds);
  const longestSeconds = num(coverage.longest_seconds);
  if (
    speechSeconds == null ||
    pictureSeconds == null ||
    longestSeconds == null ||
    !Array.isArray(coverage.stretches)
  ) {
    return null;
  }
  const stretches: NarrationCoverageStretch[] = [];
  for (const raw of coverage.stretches) {
    const stretch = row(raw);
    const start = num(stretch?.start);
    const end = num(stretch?.end);
    const seconds = num(stretch?.seconds);
    if (start == null || end == null || seconds == null || end < start) {
      return null;
    }
    stretches.push({
      start,
      end,
      seconds,
      sceneIds: Array.isArray(stretch?.scene_ids)
        ? stretch.scene_ids
            .map(sceneId)
            .filter((id): id is number => id != null)
        : [],
    });
  }
  stretches.sort((left, right) => left.start - right.start);
  return { speechSeconds, pictureSeconds, longestSeconds, stretches };
}

function readScenes(manifest: Row): CoverageScene[] {
  const scenes: CoverageScene[] = [];
  if (!Array.isArray(manifest.realized_scene_timeline)) return scenes;
  for (const raw of manifest.realized_scene_timeline) {
    const entry = row(raw);
    const start = num(entry?.start_second);
    const end = num(entry?.end_second);
    if (!entry || start == null || end == null || start < 0 || end <= start) {
      continue;
    }
    if (entry.skipped === true) continue;
    scenes.push({
      sceneId: sceneId(entry.scene_id),
      sceneType: typeof entry.scene_type === "string" ? entry.scene_type : null,
      start,
      end,
    });
  }
  return scenes;
}

function readSpeech(narration: Row | null): Interval[] {
  const speech: Interval[] = [];
  if (!narration || !Array.isArray(narration.timings)) return speech;
  for (const raw of narration.timings) {
    const timing = row(raw);
    const start = num(timing?.actual_start);
    const end = num(timing?.end);
    if (start == null || end == null || start < 0 || end <= start) continue;
    speech.push({ start, end });
  }
  return speech;
}

/**
 * The coverage to show for a parsed `echo-state.v1.json`: the composer's
 * `narration.coverage` when valid, else computed from the realized scene
 * timeline and narration timings. Null when the manifest describes another
 * render (remake guard, same as the narration plan panel), when the film has
 * no realized narration (song mode, unnarrated, or a manifest that omitted the
 * block), or when there is no counted picture.
 */
export function readNarrationCoverage(
  manifest: unknown,
  expected?: { workloadId?: string | null; finalVideoKey?: string | null },
): NarrationCoverage | null {
  const body = row(manifest);
  if (!body) return null;
  if (
    (expected?.workloadId && body.workload_id !== expected.workloadId) ||
    (expected?.finalVideoKey &&
      body.final_video_s3_key !== expected.finalVideoKey)
  ) {
    return null;
  }
  const deliveredOffsetSeconds = Math.max(
    0,
    num(body.delivered_offset_seconds) ?? 0,
  );
  const narration = row(body.narration);

  const measured = parseManifestCoverage(narration?.coverage);
  if (measured) {
    return { ...measured, source: "manifest", deliveredOffsetSeconds };
  }

  // No realized placements means we cannot tell silence from "not recorded":
  // the composer omits the block on song mode and music-only remux paths.
  const speech = readSpeech(narration);
  if (speech.length === 0) return null;
  const computed = computeNarrationCoverage({
    scenes: readScenes(body),
    speech,
    contentDurationSeconds: num(body.content_duration_seconds),
  });
  if (!computed) return null;
  return { ...computed, source: "computed", deliveredOffsetSeconds };
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/** Seconds as the operator reads them: one decimal, "10.5 s". */
export function formatCoverageSeconds(seconds: number): string {
  return `${seconds.toFixed(1)} s`;
}

/**
 * Scene ids in timeline order with consecutive runs collapsed:
 * [7, 8, 9, 10] → "7–10"; [3, 5, 6] → "3, 5–6"; a reordered [9, 3, 4] →
 * "9, 3–4" (never re-sorted, so the order matches what plays).
 */
export function formatSceneRange(ids: readonly number[]): string {
  const parts: string[] = [];
  let runStart: number | null = null;
  let runEnd: number | null = null;
  const flush = () => {
    if (runStart == null || runEnd == null) return;
    parts.push(runStart === runEnd ? `${runStart}` : `${runStart}–${runEnd}`);
  };
  for (const id of ids) {
    if (runEnd != null && id === runEnd + 1) {
      runEnd = id;
      continue;
    }
    flush();
    runStart = id;
    runEnd = id;
  }
  flush();
  return parts.join(", ");
}

/** "scenes 7–10" / "scene 4" / "" when the stretch names no scene. */
export function sceneLabel(ids: readonly number[]): string {
  if (ids.length === 0) return "";
  return `${ids.length === 1 ? "scene" : "scenes"} ${formatSceneRange(ids)}`;
}

/** The listed stretch that is the longest silence, when one is listed. */
export function longestStretch(
  coverage: NarrationCoverage,
): NarrationCoverageStretch | null {
  let best: NarrationCoverageStretch | null = null;
  for (const stretch of coverage.stretches) {
    if (!best || stretch.seconds > best.seconds) best = stretch;
  }
  return best;
}

/** "Longest silence: 10.5 s (scenes 7–10)". */
export function longestSilenceLabel(coverage: NarrationCoverage): string {
  const longest = longestStretch(coverage);
  const seconds = Math.max(coverage.longestSeconds, longest?.seconds ?? 0);
  const scenes = longest ? sceneLabel(longest.sceneIds) : "";
  return `Longest silence: ${formatCoverageSeconds(seconds)}${
    scenes ? ` (${scenes})` : ""
  }`;
}

/** True when the film has a silence the composer reports as a gap. */
export function isUnderNarrated(coverage: NarrationCoverage): boolean {
  return (
    Math.max(coverage.longestSeconds, longestStretch(coverage)?.seconds ?? 0) >=
    LONG_SILENCE_SECONDS
  );
}
